import axios, { AxiosInstance, AxiosError } from 'axios';

// Strip whitespace, real CR/LF, AND the literal 2-char strings "\n" / "\r"
// that can sneak in from Vercel/CRA env values pasted with stray escapes.
// Without this, requests go out as `/api/v1\n/auth/login` — the path
// fails to match the FastAPI router prefix and surfaces as opaque 5xx.
const sanitizeBaseUrl = (u: string): string =>
  u
    .replace(/(\\[rn])+$/g, '')      // literal "\n" / "\r" sequences at end
    .replace(/[\r\n\t\s]+$/g, '')    // real CR/LF/whitespace at end
    .replace(/^\s+/g, '');           // leading whitespace
const RAW_API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
const API_BASE_URL = sanitizeBaseUrl(RAW_API_URL);

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 1800000, // 30 dakika timeout (büyük dosya indirmeleri için)
      maxContentLength: Infinity, // Büyük dosyalar için sınırsız
      maxBodyLength: Infinity, // Büyük dosyalar için sınırsız
    });
    
    // Log API base URL for debugging
    console.log('API Base URL:', API_BASE_URL);

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle errors
    this.client.interceptors.response.use(
      (response) => {
        console.log('API Response:', response.config.method?.toUpperCase(), response.config.url, response.status);
        return response;
      },
      (error: AxiosError) => {
        console.error('API Error:', error.config?.method?.toUpperCase(), error.config?.url, error.response?.status, error.message);
        
        // Only handle 401 as authentication error (token expired/invalid)
        // 403 might be authorization (insufficient permissions) or payment errors, don't redirect
        // 402 is payment/token errors, don't redirect
        if (error.response?.status === 401) {
          // Token expired or invalid - but don't redirect if we're already on login page
          if (window.location.pathname !== '/login') {
            console.warn('Auth error (401) detected, clearing token and redirecting to login');
            // Clear all auth data first to prevent infinite redirect loops
            localStorage.removeItem('access_token');
            // Small delay to allow current error handlers (authStore.loadUser) to complete
            // before triggering a full page reload, preventing blank page flashes
            setTimeout(() => {
              if (window.location.pathname !== '/login') {
                window.location.href = '/login';
              }
            }, 100);
          }
        }
        
        return Promise.reject(error);
      }
    );
  }

  get instance(): AxiosInstance {
    return this.client;
  }
}

export const apiClient = new ApiClient();
export default apiClient.instance;

// Admin API endpoints
export const adminAPI = {
  getGlobalAnalytics: async () => {
    const response = await apiClient.instance.get('/admin/analytics');
    return response.data;
  },
  
  getGlobalStatistics: async () => {
    const response = await apiClient.instance.get('/admin/statistics');
    return response.data;
  },
  
  getUsers: async () => {
    const response = await apiClient.instance.get('/admin/users');
    return response.data;
  },
  
  getUserDetails: async (username: string) => {
    const response = await apiClient.instance.get(`/admin/users/${username}/detailed`);
    return response.data;
  },
  
  getUserActivity: async (username: string, days: number = 30) => {
    const response = await apiClient.instance.get(`/admin/users/${username}/activity?days=${days}`);
    return response.data;
  },
  
  updateUser: async (username: string, updateData: any) => {
    const response = await apiClient.instance.patch(`/admin/users/${username}`, updateData);
    return response.data;
  },
  
  getUserStatistics: async (username: string) => {
    const response = await apiClient.instance.get(`/admin/users/${username}/statistics`);
    return response.data;
  },

  getUserTokenHistory: async (username: string, limit: number = 50) => {
    const response = await apiClient.instance.get(`/admin/users/${username}/token-history?limit=${limit}`);
    return response.data;
  },
  
  getAllFeedback: async (statusFilter?: string, limit: number = 100) => {
    const params = new URLSearchParams();
    if (statusFilter) params.append('status_filter', statusFilter);
    params.append('limit', limit.toString());
    const response = await apiClient.instance.get(`/admin/feedback?${params.toString()}`);
    return response.data;
  },
  
  respondToFeedback: async (feedbackId: number, adminResponse: string, newStatus: string = 'responded') => {
    const response = await apiClient.instance.post(`/admin/feedback/${feedbackId}/respond`, {
      admin_response: adminResponse,
      new_status: newStatus,
    });
    return response.data;
  },
  
  analyzeScenarioDevelopment: async (scenarioFile: File) => {
    const formData = new FormData();
    formData.append('scenario_pdf', scenarioFile);
    
    const response = await apiClient.instance.post('/admin/development/analyze-scenario', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      responseType: 'blob', // Excel dosyası için blob
    });
    return response.data;
  },
};

// User profile API endpoints
export const profileAPI = {
  getMyActivity: async (days: number = 30) => {
    const response = await apiClient.instance.get(`/profile/activity?days=${days}`);
    return response.data;
  },
  
  getMyPreferences: async () => {
    const response = await apiClient.instance.get('/profile/preferences');
    return response.data;
  },
  
  updateMyPreferences: async (preferences: any) => {
    const response = await apiClient.instance.patch('/profile/preferences', preferences);
    return response.data;
  },
  
  submitFeedback: async (feedbackType: string, message: string, rating?: number) => {
    const response = await apiClient.instance.post('/profile/feedback', {
      feedback_type: feedbackType,
      message,
      rating,
    });
    return response.data;
  },
  
  getMyFeedback: async (limit: number = 20) => {
    const response = await apiClient.instance.get(`/profile/feedback?limit=${limit}`);
    return response.data;
  },
  
  getMyTokenHistory: async (limit: number = 50) => {
    const response = await apiClient.instance.get(`/profile/token-history?limit=${limit}`);
    return response.data;
  },
};

